import AssessmentIcon from '@mui/icons-material/Assessment';
import GradeIcon from '@mui/icons-material/Grade';
import HomeIcon from '@mui/icons-material/Home';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LogoutIcon from '@mui/icons-material/Logout';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SettingsIcon from '@mui/icons-material/Settings';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TapasIcon from '@mui/icons-material/Tapas';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Theme, useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { Role } from '../../common/enums';
import icon from '../../assets/circleIcon.png';
import build from '../../common/buildEnv';
import useAuthStore from '../../datastore/useAuth';
import { useAuthApi } from '../../hooks/api/useAuthApi';
import {
  navbarAdminItems,
  navbarEndItems,
  navbarListItemsIconEnum,
  navbarListItemsTextEnum,
  navbarMainItems,
} from './navbarData';
import { NavbarItem } from './navbarItem';

const { version, env } = build;

function getIcon(icon: unknown, isActive: boolean, theme: Theme) {
  const color = isActive
    ? 'white'
    : (theme?.palette?.primary?.main ?? '#00846D');
  switch (icon) {
    //Staff
    case navbarListItemsIconEnum.DASHBOARD:
      return <HomeIcon sx={{ color }} />;
    case navbarListItemsIconEnum.WALK_UP:
      return <TransferWithinAStationIcon sx={{ color }} />;
    case navbarListItemsIconEnum.POS:
      return <PointOfSaleIcon sx={{ color }} />;
    case navbarListItemsIconEnum.ORDER:
      return <ReceiptIcon sx={{ color }} />;
    case navbarListItemsIconEnum.CUSTOMERS:
      return <PersonIcon sx={{ color }} />;
    case navbarListItemsIconEnum.INVENTORY:
      return <InventoryIcon sx={{ color }} />;
    //Admin
    case navbarListItemsIconEnum.POPULAR:
      return <GradeIcon sx={{ color }} />;
    case navbarListItemsIconEnum.ACCOUNTS:
      return <ManageAccountsIcon sx={{ color }} />;
    case navbarListItemsIconEnum.PRODUCTS:
      return <ShoppingCartIcon sx={{ color }} />;
    case navbarListItemsIconEnum.COMPONENTS:
      return <TapasIcon sx={{ color }} />;
    case navbarListItemsIconEnum.COMPONENT_TYPES:
      return <LocalDiningIcon sx={{ color }} />;
    case navbarListItemsIconEnum.SUPPLIERS:
      return <LocalShippingIcon sx={{ color }} />;
    case navbarListItemsIconEnum.REPORTS:
      return <AssessmentIcon sx={{ color }} />;
    case navbarListItemsIconEnum.RECEIVING: // Icon for Receiving menu
      return <LocalShippingIcon sx={{ color }} />;
    //General
    case navbarListItemsIconEnum.SETTINGS:
      return <SettingsIcon sx={{ color }} />;
    case navbarListItemsIconEnum.LOG_OUT:
      return <LogoutIcon sx={{ color }} />;
    default:
      return <OpenInNewIcon sx={{ color }} />;
  }
}

export default function SideNavbar() {
  const navigate = useNavigate();
  const { role } = useAuthStore();
  const { logout } = useAuthApi();
  const theme = useTheme();
  const location = useLocation();

  function handleSelect(path: string, text: string) {
    if (text === navbarListItemsTextEnum.LOG_OUT) {
      logout();
      navigate('/')
    } else {
      navigate(path);
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '5em',
        backgroundColor: theme.palette.common.deepblue,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
        flexShrink: 0,
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'grey.700',
          borderRadius: 2,
        },
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <img
          src={icon}
          alt="Logo for TPGE"
          style={{
            width: '5em',
          }}
        />
      </Box>
      <List
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 0,
          width: '100%',
        }}
      >
        {navbarMainItems
          .filter(item => role && item.allowedRoles.includes(role as Role))
          .map((item, index) => {
            const isActive = location.pathname === item.path;
            return (
              <Tooltip
                key={index}
                title={item.tooltipText}
                placement="right"
                arrow
                enterTouchDelay={0}
              >
                <Box sx={{ width: '100%' }}>
                  <NavbarItem
                    text={item.text}
                    icon={getIcon(item.icon, isActive, theme)}
                    path={item.path}
                    handleSelect={() => handleSelect(item.path, item.text)}
                  />
                </Box>
              </Tooltip>
            );
          })}
        {navbarAdminItems
          .filter(item => role && item.allowedRoles.includes(role as Role))
          .map((item, index) => {
            const isActive = location.pathname === item.path;
            return (
              <Tooltip
                key={`admin-${index}`}
                title={item.tooltipText}
                placement="right"
                arrow
                enterTouchDelay={0}
              >
                <Box sx={{ width: '100%' }}>
                  <NavbarItem
                    text={item.text}
                    icon={getIcon(item.icon, isActive, theme)}
                    path={item.path}
                    handleSelect={() => handleSelect(item.path, item.text)}
                  />
                </Box>
              </Tooltip>
            );
          })}
      </List>
      <Box sx={{ width: '100%', mt: 'auto' }}>
        <List sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 0, width: '100%' }}>
          {navbarEndItems.map((item, index) => {
            const isActive = location.pathname === item.path;
            return (
              <Tooltip
                key={index}
                title={item.tooltipText}
                placement="right"
                arrow
                enterTouchDelay={0}
              >
                <Box sx={{ width: '100%' }}>
                  <NavbarItem
                    text={item.text}
                    icon={getIcon(item.icon, isActive, theme)}
                    path={item.path}
                    handleSelect={() => handleSelect(item.path, item.text)}
                  />
                </Box>
              </Tooltip>
            );
          })}
        </List>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 1.5,
            px: 0.5,
            width: '100%',
          }}
        >
          <Typography
            color="grey.500"
            sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}
          >
            v{version ?? ' n/a'}
          </Typography>
          <Typography color="grey.500" sx={{ fontSize: '0.8rem' }}>
            {env}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
